import React, { useEffect, useMemo, useRef } from 'react';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { useRouter } from 'next/router';
import { useSize, useUpdateEffect } from 'ahooks';
import Head from 'next/head';
import dayjs from 'dayjs';

import DrawSection from '@/components/RaffleDetails/DrawSection';
import CoreInfo from '@/components/RaffleDetails/CoreInfo';
import ProjectDetails from '@/components/RaffleDetails/ProjectDetails';
import ActiveList from '@/components/RaffleDetails/ActiveList';
import { getRaffleDetails, RaffleDetails } from 'api/raffle/raffle_detail';
import {
  setDiscordLimit,
  setEmailAddr,
  setEntries,
  setEntryCount,
  setHasWonMintWallets,
  setIsAnnounce,
  setIsOpenTwitterAutomate,
  setIsOwner,
  setRaffleDiscordUsername,
  setRaffleInfo,
  setRaffleMintWallet,
  setRaffleRegisterResult,
  setRaffleTwitterUsername,
  setTwitterAutoRequirement,
  setValidEmailAddr,
} from 'store/features/raffleDetailsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useUserInfo } from 'lib/user';
import { showMotion } from 'lib/showMotion';
import { event } from 'api/event';
import PageLayout from '@/components/PageLayout';
import { useMintWalletRaffle } from 'api/user_profile/user_profile_address_list';
import {
  autoRequirement,
  AutoRequirementData,
} from 'api/raffle/raffle_entry_auto_requirement';
import { setMintWalletListRaffle } from 'store/features/userSlice';

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  if (ctx.query.vanity_url) {
    return {
      redirect: {
        destination: `/r/${ctx.query.vanity_url}`,
        permanent: true,
      },
    };
  }
  const raffleDetailsInfo: RaffleDetails = await getRaffleDetails(
    ctx.query.vanityUrl as string,
    ctx.req?.cookies?.Authorization as string
  );
  const raffleAutoRequirement: AutoRequirementData = await autoRequirement(
    ctx.req?.cookies?.Authorization as string
  );

  if (!raffleDetailsInfo) {
    return {
      redirect: {
        destination: '/404.html',
        permanent: false,
      },
    };
  }

  return {
    props: {
      raffleDetailsInfo: raffleDetailsInfo || {},
      raffleAutoRequirement: raffleAutoRequirement || {},
    },
  };
}

const RaffleDetails = ({
  raffleDetailsInfo,
  raffleAutoRequirement,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { getMintWalletRaffle } = useMintWalletRaffle();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const ref = useRef<HTMLDivElement>(null);
  const size = useSize(ref);
  const {
    isLogin,
    address,
    email,
    discord_username,
    twitter_username,
    twitter_write_permission,
  } = useUserInfo();
  const { mintWalletListRaffle } = useAppSelector((state) => state.user);
  const { mintWallet } = useAppSelector((state) => state.raffleDetails);

  useUpdateEffect(() => {
    location.reload();
  }, [isLogin]);

  useEffect(() => {
    getMintWalletRaffle(raffleDetailsInfo?.raffle?.project?.blockchain || '');
  }, []);

  useEffect(() => {
    event({
      event: 'page_view',
      uid: localStorage.getItem('uuid'),
      address: address,
      biz: 'Luckyplus',
      page: 'details',
      timestamp: Date.now(),
      extra1: raffleDetailsInfo?.raffle?.id,
      extra2: router.query.vanityUrl,
      extra4: localStorage.getItem('uuid'),
    });
  }, [address, raffleDetailsInfo?.raffle?.id, router.query.vanityUrl]);

  useMemo(() => {
    if (typeof window !== 'undefined' && !!raffleDetailsInfo) {
      dispatch(setRaffleInfo(raffleDetailsInfo?.raffle));
      if (!raffleDetailsInfo?.raffle?.is_hide_entry_count) {
        dispatch(setEntryCount(raffleDetailsInfo?.raffle?.entry_count));
      }
      if (!!raffleDetailsInfo?.raffle?.my_entry) {
        dispatch(setEntries(raffleDetailsInfo?.raffle?.my_entry?.multiplier));
        if (!mintWallet) {
          dispatch(
            setRaffleMintWallet(
              raffleDetailsInfo?.raffle?.my_entry?.mint_wallet
            )
          );
        }
        dispatch(
          setRaffleDiscordUsername(
            raffleDetailsInfo?.raffle?.my_entry?.discord_username
          )
        );
        dispatch(
          setRaffleTwitterUsername(
            raffleDetailsInfo?.raffle?.my_entry?.twitter_username
          )
        );
        if (!!raffleDetailsInfo?.raffle?.my_entry?.email) {
          dispatch(setEmailAddr(raffleDetailsInfo?.raffle?.my_entry?.email));
          dispatch(setValidEmailAddr(1));
        }
      } else {
        if (!mintWallet) {
          dispatch(
            setRaffleMintWallet(
              mintWalletListRaffle?.filter((item) => item?.is_default === 1)[0]
                ?.address || mintWalletListRaffle[0]?.address
            )
          );
        }
        dispatch(setRaffleDiscordUsername(discord_username));
        dispatch(setRaffleTwitterUsername(twitter_username));
        if (!!email) {
          dispatch(setEmailAddr(email));
          dispatch(setValidEmailAddr(1));
        } else if (!!localStorage.getItem('lastFillEmail')) {
          dispatch(
            setEmailAddr(localStorage.getItem('lastFillEmail') as string)
          );
          dispatch(setValidEmailAddr(1));
        }
      }
      dispatch(
        setIsOpenTwitterAutomate(
          twitter_write_permission &&
            !!raffleDetailsInfo?.raffle?.requirement_is_tw_open &&
            !!raffleAutoRequirement?.twitter
        )
      );
      dispatch(
        setIsOwner(
          !!raffleDetailsInfo?.is_owner ||
            !!raffleDetailsInfo?.is_community_team_member
        )
      );
      dispatch(
        setIsAnnounce(raffleDetailsInfo?.raffle?.is_dc_new_raffle_announced)
      );
      dispatch(setHasWonMintWallets(raffleDetailsInfo?.has_won_mint_wallets));
      dispatch(setTwitterAutoRequirement(!!raffleAutoRequirement?.twitter));
    }
  }, [
    discord_username,
    dispatch,
    email,
    mintWallet,
    mintWalletListRaffle,
    raffleAutoRequirement?.twitter,
    raffleDetailsInfo,
    twitter_username,
    twitter_write_permission,
  ]);

  useEffect(() => {
    if (
      !!raffleDetailsInfo?.raffle?.my_entry &&
      ((raffleDetailsInfo?.raffle?.my_entry?.is_win &&
        raffleDetailsInfo?.raffle?.status === 'FINALIZED') ||
        raffleDetailsInfo?.raffle?.type === 'WALLET_COLLECTION')
    ) {
      showMotion();
    }
  }, [
    raffleDetailsInfo?.raffle?.my_entry,
    raffleDetailsInfo?.raffle?.status,
    raffleDetailsInfo?.raffle?.type,
  ]);

  useEffect(() => {
    const exitingFunction = () => {
      dispatch(
        setRaffleRegisterResult({
          requirement_validations: [],
          multiplier: 1,
          validation: false,
        })
      );
      dispatch(setRaffleMintWallet(''));
      dispatch(setMintWalletListRaffle([]));
      dispatch(setDiscordLimit({ limitTime: 0, limitMin: '0', limitSec: '0' }));
    };

    router.events.on('routeChangeStart', exitingFunction);

    return () => {
      router.events.off('routeChangeStart', exitingFunction);
    };
  }, [router.events, dispatch]);

  const mintLocalFormat = useMemo(() => {
    if (
      !!raffleDetailsInfo?.raffle?.project?.mint_date &&
      !!raffleDetailsInfo?.raffle?.project?.mint_time
    ) {
      return `Mint date:${dayjs(
        `${dayjs
          .utc(raffleDetailsInfo?.raffle?.project?.mint_date)
          .format('YYYY-MM-DD')}T${
          raffleDetailsInfo?.raffle?.project?.mint_time
        }Z`
      )
        .utc()
        .local()
        .format('MM/DD/YYYY HH:mm:ss')}(UTC)`;
    } else if (
      !!raffleDetailsInfo?.raffle?.project?.mint_date &&
      !raffleDetailsInfo?.raffle?.project?.mint_time
    ) {
      return `Mint date:${dayjs(
        raffleDetailsInfo?.raffle?.project?.mint_date
      ).format('MM/DD/YYYY')}`;
    } else {
      return '';
    }
  }, [
    raffleDetailsInfo?.raffle?.project?.mint_date,
    raffleDetailsInfo?.raffle?.project?.mint_time,
  ]);

  const supplyCount = useMemo(() => {
    if (!!raffleDetailsInfo?.raffle?.project?.supply_count) {
      return `Supply:${raffleDetailsInfo?.raffle?.project?.supply_count}, `;
    } else {
      return '';
    }
  }, [raffleDetailsInfo?.raffle?.project?.supply_count]);

  const whitelistPrice = useMemo(() => {
    if (
      !raffleDetailsInfo?.raffle?.project?.whitelist_price &&
      Number(raffleDetailsInfo?.raffle?.project?.whitelist_price) !== 0
    ) {
      return '';
    } else {
      if (Number(raffleDetailsInfo?.raffle?.project?.whitelist_price) === 0) {
        return 'Whitelist Price:Free';
      } else {
        return `Whitelist Price:${raffleDetailsInfo?.raffle?.project?.whitelist_price} ETH`;
      }
    }
  }, [raffleDetailsInfo?.raffle?.project?.whitelist_price]);

  const descriptionRender = useMemo(() => {
    const lines = String(raffleDetailsInfo?.raffle?.description).split('\n');
    const formattedString = lines.slice(0, 3).join('\n');
    return lines?.length > 3 ? `${formattedString}...` : formattedString;
  }, [raffleDetailsInfo?.raffle?.description]);

  const bannerRender = useMemo(() => {
    if (!!raffleDetailsInfo?.raffle?.project?.banner) {
      return raffleDetailsInfo?.raffle?.project?.banner;
    } else {
      return 'https://s.luckygo.io/luckybuy/img/detailDefaultBanner2.png';
    }
  }, [raffleDetailsInfo?.raffle?.project?.banner]);

  return (
    <>
      <Head>
        <title>LuckyGo</title>
        <meta name="og:site_name" content="LuckyGo"></meta>
        <meta
          property="og:url"
          content={process.env.NEXT_PUBLIC_LUCKYB_INEDX}
        />
        <meta property="og:title" content={raffleDetailsInfo?.raffle?.name} />
        <meta property="og:description" content={descriptionRender} />
        <meta property="og:image" content={bannerRender} />
        <meta property="og:type" content="website" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:site" content="@LuckyPlus_io" />
        <meta
          property="twitter:url"
          content={process.env.NEXT_PUBLIC_LUCKYB_INEDX}
        />
        <meta
          property="twitter:title"
          content={raffleDetailsInfo?.raffle?.name}
        />
        <meta
          property="twitter:description"
          content={`${mintLocalFormat} ${supplyCount} ${whitelistPrice}`}
        />
        <meta property="twitter:image" content={bannerRender} />
      </Head>
      <div
        className="h-[100vh] w-[100vw] overflow-y-scroll"
        ref={ref}
        id="pageTop"
      >
        <PageLayout>
          <div className="lg:grid lg:grid-cols-12 lg:gap-6">
            <div className="lg:col-span-8">
              <CoreInfo></CoreInfo>
              {Number(size?.width) >= 768 && <ProjectDetails></ProjectDetails>}
            </div>
            <div className="lg:col-span-4">
              <DrawSection
                list={raffleDetailsInfo?.project_other_active_raffles}
                screenWidth={Number(size?.width)}
                ownList={raffleDetailsInfo?.you_won_raffles}
              ></DrawSection>
            </div>
            {Number(size?.width) < 768 && <ProjectDetails></ProjectDetails>}
            {Number(size?.width) < 768 && (
              <ActiveList
                otherActiveList={
                  raffleDetailsInfo?.project_other_active_raffles
                }
                ownList={raffleDetailsInfo?.you_won_raffles}
              />
            )}
          </div>
        </PageLayout>
      </div>
    </>
  );
};

export default RaffleDetails;
